// CORE
// Este é um arquivo core do boilerplate e modificações neste arquivo serão bloqueadas via pré-hook de commit.
// Fazemos este bloqueio para manter a consistência do boilerplate e diminuir as chances de conflitos nos updates.
// Caso possível, procure uma alternativa ao uso deste arquivo.
// Se a alteração for realmente necessária ou for um update do boilerplate,
// realize o commit usando o comando 'git commit --no-verify'.

import { commonColors, neutralColor } from '@/config/theme/colors';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React from 'react';

const StyledBox = styled(Box)(({ theme, variant, color, size = 'medium' }) => {
  const colorGroup = commonColors[color] || commonColors.blueProper;

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          height: '16px',
          width: 'fit-content',
          padding: theme.spacing(0, 0.5),
          '& .MuiTypography-root': {
            ...theme.typography['label-tiny'],
            lineHeight: '16px',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '10px',
          },
        };
      case 'large':
        return {
          height: '32px',
          width: 'fit-content',
          padding: theme.spacing(0.5, 1.25),
          '& .MuiTypography-root': {
            ...theme.typography['label-medium'],
            lineHeight: '24px',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '18px',
          },
        };
      default: // medium
        return {
          height: '24px',
          width: 'fit-content',
          padding: theme.spacing(0.25, 1),
          '& .MuiTypography-root': {
            ...theme.typography['label-small'],
            lineHeight: '20px',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '14px',
          },
        };
    }
  };

  const getStyles = (overrideVariant) => {
    const effectiveVariant =
      overrideVariant !== undefined ? overrideVariant : variant;
    switch (effectiveVariant) {
      case 'inverted':
        switch (color) {
          case 'green':
          case 'yellow':
          case 'orange':
            return {
              backgroundColor: colorGroup.dark,
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: neutralColor[0],
              },
              '& .MuiBox-root': {
                backgroundColor: colorGroup.dark,
                background: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), ${colorGroup.dark}`,
                color: neutralColor[0],
              },
            };
          case 'white':
            return {
              backgroundColor: neutralColor[900],
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: neutralColor[0],
              },
              '& .MuiBox-root': {
                backgroundColor: neutralColor[1000],
                color: neutralColor[0],
              },
            };
          case 'gray':
            return {
              backgroundColor: neutralColor[800],
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: neutralColor[0],
              },
              '& .MuiBox-root': {
                backgroundColor: neutralColor[900],
                color: neutralColor[0],
              },
            };
          default:
            return {
              backgroundColor: colorGroup.regular,
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: neutralColor[0],
              },
              '& .MuiBox-root': {
                backgroundColor: colorGroup.dark,
                color: neutralColor[0],
              },
            };
        }

      case 'outlined': {
        // Get base styles using default variant
        const baseVariant = variant;
        variant = 'default';
        const defaultStyles = getStyles();
        variant = baseVariant;

        return {
          ...defaultStyles,
          border: `1px solid ${defaultStyles['& .MuiTypography-root, & .MuiSvgIcon-root'].color}`,
        };
      }

      default:
        switch (color) {
          case 'green':
            return {
              backgroundColor: colorGroup.darkerHighlight,
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: colorGroup.dark,
              },
              '& .MuiBox-root': {
                backgroundColor: neutralColor[0],
                color: colorGroup.dark,
              },
            };
          case 'yellow':
          case 'orange':
            return {
              backgroundColor: colorGroup.highlight,
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: colorGroup.dark,
              },
              '& .MuiBox-root': {
                backgroundColor: neutralColor[0],
                color: colorGroup.dark,
              },
            };
          case 'white':
            return {
              backgroundColor: neutralColor[0],
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: neutralColor[1000],
              },
              '& .MuiBox-root': {
                backgroundColor: neutralColor[200],
                color: neutralColor[1000],
              },
            };
          case 'gray':
            return {
              backgroundColor: neutralColor[200],
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: neutralColor[800],
              },
              '& .MuiBox-root': {
                backgroundColor: neutralColor[0],
                color: neutralColor[800],
              },
            };
          default:
            return {
              backgroundColor: colorGroup.highlight,
              '& .MuiTypography-root, & .MuiSvgIcon-root': {
                color: colorGroup.regular,
              },
              '& .MuiBox-root': {
                backgroundColor: neutralColor[0],
                color: colorGroup.regular,
              },
            };
        }
    }
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    gap: theme.spacing(0.5),
    borderRadius: '16px',
    ...getSizeStyles(),
    ...getStyles(),
  };
});

export default function Label({
  icon,
  text,
  chipText,
  onAction,
  actionIcon = 'arrow',
  variant = 'default',
  color = 'blue',
  size = 'medium',
  maxWidth = '200px',
}) {
  if (!icon && !text) {
    throw new Error('Either icon or text must be provided to Label component');
  }

  const ActionIcon =
    actionIcon === 'arrow' ? KeyboardArrowDownIcon : CloseIcon;

  const renderIcon = () => {
    if (!icon) return null;

    if (React.isValidElement(icon)) return icon;

    return React.createElement(icon, { fontSize: 'small' });
  };

  return (
    <StyledBox variant={variant} color={color} size={size}>
      {renderIcon()}
      {text && (
        <Typography
          variant={`label-${size}`}
          sx={{
            maxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </Typography>
      )}
      {chipText && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: '8px',
            overflow: 'hidden',
            padding: '0 4px',
            height: {
              small: '12px',
              medium: '16px',
              large: '20px',
            }[size],
            '& > .MuiTypography-root': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: {
                small: '10px',
                medium: '12px',
                large: '14px',
              }[size],
              lineHeight: {
                small: '12px',
                medium: '16px',
                large: '20px',
              }[size],
              px: {
                small: 0.5,
                medium: 0.6,
                large: 1,
              },
            },
          }}
        >
          <Typography
            variant={`label-${size === 'large' ? 'medium' : 'tiny'}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {chipText}
          </Typography>
        </Box>
      )}
      {onAction && (
        <IconButton
          size='small'
          onClick={onAction}
          sx={{
            padding: 0,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <ActionIcon fontSize='small' />
        </IconButton>
      )}
    </StyledBox>
  );
}

Label.propTypes = {
  /** Icon component from MUI icons */
  icon: PropTypes.oneOfType([
    PropTypes.elementType, // componente (e.g., CloseIcon)
    PropTypes.element, // elemento (e.g., <CloseIcon />)
  ]),
  /** Main text content */
  text: PropTypes.string,
  /** Optional chip text (usually a number) */
  chipText: PropTypes.string,
  /** Action function when clicking the action icon */
  onAction: PropTypes.func,
  /** Type of action icon to display */
  actionIcon: PropTypes.oneOf(['arrow', 'close']),
  /** Visual style variant */
  variant: PropTypes.oneOf(['default', 'inverted', 'outlined']),
  /** Color theme to use */
  color: PropTypes.oneOf([
    'blue',
    'green',
    'red',
    'yellow',
    'orange',
    'purple',
    'gray',
    'white',
  ]),
  /** Size of the label */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};
